import { useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import UserSelector from "components/shared/UserSelector";
import { useModals } from "ModalProvider";
import { useContext } from "react";
import { BsTrash } from "react-icons/bs";
import AccountForm from "./accounts/AccountForm";
import { CustomerContext } from "./CustomerPage";
import {
  SALES_ADD_CUSTOMER_SUB_ACCOUNT,
  SALES_REMOVE_CUSTOMER_SUB_ACCOUNT,
} from "./graphql";

const SubAccountsView = () => {
  const modal = useModals();

  const { customer } = useContext(CustomerContext);
  const [addSubAccount, addSubAccountRes] = useMutation(
    SALES_ADD_CUSTOMER_SUB_ACCOUNT,
    {
      onCompleted() {
        Alert("success", "Sub account added successfully.");
      },
      onError(error) {
        Alert("error", parseError(error));
      },
    },
  );
  const [removeAccount, removeAccountRes] = useMutation(
    SALES_REMOVE_CUSTOMER_SUB_ACCOUNT,
    {
      onCompleted() {
        Alert("success", "Sub account removed successfully.");
      },
      onError(error) {
        Alert("error", parseError(error));
      },
    },
  );

  function tryCreateSubAccount() {
    modal.present({
      title: "Create New Sub Account",
      subtitle: "Create a new sub account for this customer.",
      children: <AccountForm onCreated={didChooseSubAccount} />,
    });
  }

  function tryChooseSubAccount() {
    modal.present({
      title: "Choose Account",
      subtitle: "Select an existing user to associate with this customer.",
      children: (
        <UserSelector
          onSelectUser={didChooseSubAccount}
          fetchPolicy="network-only"
        />
      ),
    });
  }

  function didChooseSubAccount(user) {
    modal.hide();
    addSubAccount({
      variables: { customerId: customer.id, userId: user.id },
    });
  }

  function tryRemoveSubAccount(subAccount) {
    if (
      window.confirm(
        `Are you sure you want to remove ${subAccount.user.email} from this customer? This will not delete the user account, but will remove their access to this customer's information.`,
      )
    ) {
      removeAccount({
        variables: {
          customerId: customer.id,
          accountId: subAccount.id,
        },
      });
    }
  }

  return (
    <div className="pt-2">
      <h6>Sub Accounts</h6>
      <div className="opacity-70">
        Sub accounts can access the same customer's information in biz.
      </div>
      <div className="mt-1">
        {customer.subAccounts.length > 0 ? (
          <div className="-mx-2">
            <table>
              <tbody>
                {customer.subAccounts.map((subAccount, index) => (
                  <tr
                    key={index}
                    className="space-y-1 border-b border-gray-100 dark:border-gray-700"
                  >
                    <td className="font-semibold">{subAccount.user.email}</td>
                    <td>
                      <div className="flex justify-end">
                        <Button
                          danger
                          onClick={() => tryRemoveSubAccount(subAccount)}
                        >
                          <BsTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>This customer does not have any sub accounts.</div>
        )}

        <div className="flex space-x-4 mt-2">
          {addSubAccountRes.loading ? (
            <InlineSpinner size={14} />
          ) : (
            <>
              <Button
                onClick={tryCreateSubAccount}
                loading={addSubAccountRes.loading}
              >
                Create New
              </Button>
              <Button
                onClick={tryChooseSubAccount}
                loading={addSubAccountRes.loading}
              >
                Choose
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubAccountsView;
